import type {
  SmartCreationAdGroupDraftContentV2,
  SmartCreationDraftContentV2,
} from "../schemas/smart-creation";
import { compareText, hash } from "./hashing";
import type { ReviewTarget, ReviewTargeting } from "./types";

export const LINEAGE_VERSION = 1 as const;

export function lineage(
  kind: "campaign" | "ad-group" | "target" | "negative",
  value: unknown,
): string {
  return `review:v${LINEAGE_VERSION}:${kind}:${hash(value)}`;
}

export function targetStableKey(value: unknown): string {
  return `sc:v1:target:${hash(value)}`;
}

export function targetingLane(targeting: ReviewTargeting): string {
  return targeting.type === "keyword" ? `keyword:${targeting.matchType}` : targeting.type;
}

export function productsKey(adGroup: SmartCreationAdGroupDraftContentV2): string[] {
  return adGroup.products.map((product) => product.sku).sort(compareText);
}

export function campaignLineage(
  campaign: SmartCreationDraftContentV2["campaigns"][number],
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
  precise = false,
): string {
  const adGroupSemantics = campaign.adGroups
    .map((adGroup) => ({
      products: productsKey(adGroup),
      ...(precise
        ? {
            targetIdentity: adGroup.targeting.targets.map((target) => ({
              sourceTargetId: target.sourceTargetId,
              strategyKind: target.strategyKind,
            })),
          }
        : {}),
      lane: targetingLane(adGroup.targeting),
      ...(previewGeneratorVersion >= 3
        ? { strategyKind: adGroup.targeting.targets[0]?.strategyKind }
        : {}),
    }))
    .sort((left, right) => compareText(JSON.stringify(left), JSON.stringify(right)));
  return lineage("campaign", { targetingType: campaign.targetingType, adGroupSemantics });
}

export function adGroupLineage(
  adGroup: SmartCreationAdGroupDraftContentV2,
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
  precise = false,
): string {
  return lineage("ad-group", {
    products: productsKey(adGroup),
    ...(precise
      ? {
          targetIdentity: adGroup.targeting.targets.map((target) => ({
            sourceTargetId: target.sourceTargetId,
            strategyKind: target.strategyKind,
          })),
        }
      : {}),
    lane: targetingLane(adGroup.targeting),
    ...(previewGeneratorVersion >= 3
      ? { strategyKind: adGroup.targeting.targets[0]?.strategyKind }
      : {}),
  });
}

export function targetLineage(
  adGroup: SmartCreationAdGroupDraftContentV2,
  target: ReviewTarget,
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
): string {
  return lineage("target", {
    products: productsKey(adGroup),
    sourceTargetId: target.sourceTargetId,
    // Preview generator version 3 isolates Strategies and can fan one source into B/P/E groups.
    // Older snapshots keep their original lineage and content hashes verbatim.
    ...(previewGeneratorVersion >= 3
      ? { strategyKind: target.strategyKind, lane: targetingLane(adGroup.targeting) }
      : {}),
  });
}

export function campaignRef(
  campaign: SmartCreationDraftContentV2["campaigns"][number],
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
  precise = false,
) {
  return {
    kind: "campaign" as const,
    itemKey: campaign.itemKey,
    lineageKey: campaignLineage(campaign, previewGeneratorVersion, precise),
  };
}

export function adGroupRef(
  adGroup: SmartCreationAdGroupDraftContentV2,
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
  precise = false,
) {
  return {
    kind: "ad_group" as const,
    itemKey: adGroup.itemKey,
    lineageKey: adGroupLineage(adGroup, previewGeneratorVersion, precise),
  };
}

export function targetRef(
  adGroup: SmartCreationAdGroupDraftContentV2,
  target: ReviewTarget,
  previewGeneratorVersion: SmartCreationDraftContentV2["previewGeneratorVersion"],
) {
  return {
    kind: "target" as const,
    itemKey: target.itemKey,
    lineageKey: targetLineage(adGroup, target, previewGeneratorVersion),
  };
}
